import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, HeartOff, Search, X, MapPin, Clock, Zap } from 'lucide-react';
import JobDetailModal from './JobDetailModal';

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

function savedBucketFor(savedAt) {
    const now = new Date();
    const saved = new Date(savedAt);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (now.toDateString() === saved.toDateString()) return 'today';
    if (yesterday.toDateString() === saved.toDateString()) return 'yesterday';
    const diffDays = Math.floor((now - saved) / 86400000);
    if (diffDays < 7) return 'week';
    return 'earlier';
}

function savedAgoLabel(savedAt) {
    if (!savedAt) return '';
    const diffMs = Date.now() - new Date(savedAt).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(savedAt).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
}

function formatDate(dt) {
    if (!dt) return '';
    try { return new Date(dt).toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }); }
    catch { return dt; }
}

function jobTags(job) {
    if (!job?.tags) return [];
    return job.tags.split(',').map(t => t.trim()).filter(Boolean);
}

const GROUPS = [
    { id: 'today',     label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week',      label: 'Earlier this week' },
    { id: 'earlier',   label: 'Earlier' },
];

const SavedJobs = () => {
    const [savedItems, setSavedItems]   = useState([]);
    const [loading, setLoading]         = useState(true);
    const [query, setQuery]             = useState('');
    const [sortBy, setSortBy]           = useState('recent');
    const [detailJobId, setDetailJobId] = useState(null);
    const [toast, setToast]             = useState('');
    const navigate = useNavigate();

    const flash = msg => { setToast(msg); setTimeout(() => setToast(''), 2400); };

    useEffect(() => {
        const token = getToken();
        if (!token) { navigate('/login'); return; }
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/saved-jobs`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then(r => setSavedItems(r.data ?? []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, []);

    const unsave = async (jobId, title) => {
        const token = getToken();
        setSavedItems(prev => prev.filter(s => s.job.id !== jobId));
        flash(`Removed "${title}" from saved.`);
        try {
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/saved-jobs/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch { /* already removed from UI */ }
    };

    const q = query.trim().toLowerCase();
    let visible = savedItems.filter(s =>
        !q || (`${s.job?.title || ''} ${s.job?.address || ''} ${s.job?.tags || ''}`).toLowerCase().includes(q)
    );

    if (sortBy === 'pay-high') {
        visible = [...visible].sort((a, b) => (b.job?.payment || 0) - (a.job?.payment || 0));
    }

    const grouped = GROUPS.map(g => ({
        ...g,
        items: visible.filter(s => savedBucketFor(s.savedAt) === g.id),
    })).filter(g => g.items.length > 0);

    const totalPay = savedItems.reduce((sum, s) => sum + (s.job?.payment || 0), 0);
    const detailItem = savedItems.find(s => s.job?.id === detailJobId);

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-500)' }}>
            Loading saved jobs…
        </div>
    );

    return (
        <div className="sj-page">
            <main className="sj-main">
                <div className="ix-container">

                    {/* Hero */}
                    <header className="sj-hero">
                        <div className="sj-hero-eyebrow mono">
                            <Heart size={12} fill="currentColor" /> Your collection
                        </div>
                        <div className="sj-hero-titlewrap">
                            <h1 className="sj-hero-title">
                                Saved<span className="sj-hero-dot">.</span>
                            </h1>
                            <div className="sj-hero-meta">
                                <div className="sj-hero-count">
                                    <span className="sj-hero-count-n">{String(savedItems.length).padStart(2, '0')}</span>
                                    <span className="sj-hero-count-l mono">{savedItems.length === 1 ? 'job kept' : 'jobs kept'}</span>
                                </div>
                                {totalPay > 0 && (
                                    <div className="sj-hero-stat">
                                        <span className="sj-hero-stat-n mono">€{totalPay.toFixed(0)}</span>
                                        <span className="sj-hero-stat-l mono">if you applied to all</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <p className="sj-hero-sub">
                            Bookmarks live here until you apply or remove them.{' '}
                            <a href="/get-job" onClick={e => { e.preventDefault(); navigate('/get-job'); }} className="sj-hero-link">
                                Browse more jobs
                            </a>
                        </p>
                    </header>

                    {/* Toolbar */}
                    {savedItems.length > 0 && (
                        <div className="sj-toolbar">
                            <div className="sj-search">
                                <Search size={14} />
                                <input
                                    placeholder="Filter your saved jobs…"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                />
                                {query && (
                                    <button className="sj-search-clear" onClick={() => setQuery('')} aria-label="Clear">
                                        <X size={12} />
                                    </button>
                                )}
                            </div>

                            <div className="sj-sort mono">
                                <span>Sort</span>
                                <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                                    <option value="recent">Recently saved</option>
                                    <option value="pay-high">Pay (high to low)</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    {visible.length === 0 ? (
                        <SJEmpty cleared={savedItems.length === 0} onReset={() => { setQuery(''); }} navigate={navigate} />
                    ) : (
                        <div className="sj-index">
                            {grouped.map(g => (
                                <section key={g.id} className="sj-group">
                                    <header className="sj-group-head">
                                        <span className="sj-group-label mono">{g.label}</span>
                                        <span className="sj-group-count mono">{String(g.items.length).padStart(2, '0')}</span>
                                    </header>
                                    <ol className="sj-rows">
                                        {g.items.map((s, i) => (
                                            <SJRow
                                                key={s.id}
                                                n={visible.indexOf(s) + 1}
                                                saved={s}
                                                onUnsave={() => unsave(s.job.id, s.job.title)}
                                                onView={() => setDetailJobId(s.job.id)}
                                            />
                                        ))}
                                    </ol>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Job detail modal — same as GetJob */}
            {detailJobId && (
                <JobDetailModal
                    jobId={detailJobId}
                    onClose={() => setDetailJobId(null)}
                    isSaved={true}
                    onToggleSave={() => {
                        if (detailItem) unsave(detailJobId, detailItem.job.title);
                        setDetailJobId(null);
                    }}
                />
            )}

            {toast && <div className="sj-toast">{toast}</div>}
        </div>
    );
};

/* ── Row ── */
function SJRow({ n, saved, onUnsave, onView }) {
    const j = saved.job;
    const tags = jobTags(j);

    return (
        <li className="sj-row">
            <div className="sj-row-rail">
                <span className="sj-row-n mono">{String(n).padStart(2, '0')}</span>
                <span className="sj-row-saved mono">{savedAgoLabel(saved.savedAt)}</span>
            </div>

            <div className="sj-row-body">
                <div className="sj-row-tagline">
                    {tags[0] && <span className="gj-chip-cat mono">{tags[0]}</span>}
                    {tags.slice(1, 3).map(t => (
                        <span key={t} className="gj-chip-cat mono" style={{ opacity: 0.7 }}>{t}</span>
                    ))}
                </div>
                <h3 className="sj-row-title">{j.title}</h3>
                <div className="sj-row-meta mono">
                    {j.address && <span><MapPin size={11} /> {j.address}</span>}
                    {j.address && j.dateTime && <span className="sj-row-meta-dot" aria-hidden>·</span>}
                    {j.dateTime && <span><Clock size={11} /> {formatDate(j.dateTime)}</span>}
                    {j.duration && <><span className="sj-row-meta-dot" aria-hidden>·</span><span><Zap size={11} /> {j.duration}</span></>}
                </div>
            </div>

            <div className="sj-row-pay">
                <div className="sj-row-pay-amt">€{j.payment?.toFixed(0) ?? '—'}</div>
                <div className="sj-row-pay-rate mono">Fixed{j.duration ? ` · ${j.duration}` : ''}</div>
            </div>

            <div className="sj-row-actions">
                <button className="sj-row-unsave" onClick={onUnsave} aria-label="Remove from saved" title="Remove from saved">
                    <Heart size={16} fill="currentColor" />
                </button>
                <button className="ix-btn ix-btn-outline ix-btn-sm sj-row-view" onClick={onView}>
                    View details
                </button>
            </div>
        </li>
    );
}

/* ── Empty state ── */
function SJEmpty({ cleared, onReset, navigate }) {
    if (cleared) {
        return (
            <div className="sj-empty">
                <div className="sj-empty-mark">
                    <Heart size={28} />
                </div>
                <h3 className="sj-empty-title">Nothing saved yet.</h3>
                <p className="sj-empty-body">
                    Tap the heart on any job in Get Job and it'll wait here until you're ready to apply.
                </p>
                <button className="ix-btn ix-btn-primary ix-btn-sm" onClick={() => navigate('/get-job')}>
                    Browse jobs
                </button>
            </div>
        );
    }
    return (
        <div className="sj-empty">
            <h3 className="sj-empty-title">No matches.</h3>
            <p className="sj-empty-body">Try a different search term or clear what's set.</p>
            <button className="ix-btn ix-btn-outline ix-btn-sm" onClick={onReset}>Reset filters</button>
        </div>
    );
}

export default SavedJobs;
